import React, {
    ReactNode,
    useState,
    useEffect,
    useMemo,
    useRef
} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    Switch,
    Typography,
    Form,
    Input,
    Select,
    Button,
    Spin,
    Grid,
    Tag
} from 'antd';
import {CloseOutlined, CheckOutlined} from '@ant-design/icons';
import {Store} from 'rc-field-form/lib/interface';
import {useHistory} from 'react-router-dom';

import {
    submitGeneralInfo,
    setShowPreferredEmail,
    setShowPreferredPronouns,
    setShowPrimaryCampus,
    setShowDescription,
    setShowLanguages,
    setAcceptingGraduateStudent,
    loadGeneralInfo,
} from './GeneralPanelSlice';
import {
    languageList,
    primaryCampusList,
    RESEARCHER_INTERESTS_MAX_LENGTH,
    RESEARCHER_NOTE_MAX_LENGTH,
    RESEARCHER_PREFERRED_FIRST_NAME_MAX_LENGTH,
    RESEARCHER_PREFERRED_LAST_NAME_MAX_LENGTH,
    RESEARCHER_PREFERRED_EMAIL_MAX_LENGTH,
    RESEARCHER_PREFERRED_PRONOUNS_MAX_LENGTH,
    RESEARCHER_SFU_LINK_MAX_LENGTH,
    TINYMCE_EDITOR_HTML_STRING_LIMIT,
} from '../../../../utils/constants';
import {RootState} from "../../../../app/store";
// import resultListS from '../../search_page/components/TabBlock/ResultList/ResultListSlice';
import Error500 from "../../../../components/errors/Error500";
import ToHomeButton from "../../../../components/ToHomeButton";
import TinymceEditor from '../../../../components/researcher/TinymceEditor';
import { GeneralInfoInputLengths } from '../../../../utils/types';
import { beforeCurrentYear } from 'utils/restrictYears';

const tagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={beforeCurrentYear(value)?"red":"cyan"}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };


export default () => {
    const [editorValue, setEditorValue] = useState<string>('');
    const [inputLengths, setInputLengths] = useState<GeneralInfoInputLengths>({
        preferred_first_name: 0,
        preferred_last_name: 0,
        preferred_email: 0,
        preferred_pronouns: 0,
        note: 0,
        interests: 0,
        sfu_link: 0
    });
    const researchDescriptionRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const history = useHistory();
    const screens = Grid.useBreakpoint();
    const breakpoint = screens.md;

    const textareaRows = breakpoint ? {
        note: 3,
        interests: 5,
    } : {
        note: 5,
        interests: 7,
    };

    const {user} = useSelector((state:RootState) => state.authentication);

    const {
        generalInfo,
        loading,
        error
    } = useSelector((state: RootState) => state.generalPanel);

    useEffect(() => {
        if (user?.show_to_public) {
            dispatch(loadGeneralInfo(form, setEditorValue, setInputLengths));
        }
        // return () => {
        //     dispatch(resultListS.actions.resetListData());
        // };
    }, [dispatch, form, user]);

    const languageOptions = useMemo(() => {
        const languageOptions: ReactNode[] = [];
        languageList.forEach(entry => languageOptions.push(
            <Select.Option className="sub-heading-font" key={entry} value={entry}>
                {entry}
            </Select.Option>
        ));
        return languageOptions;
    }, []);

    const recruitmentYearsOptions = useMemo(()=>{
        const curYear = new Date().getFullYear();
        const options=[];
        for(let yDiff=0;yDiff<=2;yDiff++){
            options.push({
                label: `${curYear+yDiff}`,
                value: curYear+yDiff
            });
        }
        return options;
    }, [generalInfo.recruitment.years]);

    const primaryCampusOptions = useMemo(() => {
        const primaryCampusOptions: ReactNode[] = [];
        primaryCampusList.forEach(entry => primaryCampusOptions.push(
            <Select.Option className="sub-heading-font" key={entry} value={entry}>
                {entry}
            </Select.Option>
        ));
        return primaryCampusOptions;
    }, []);

    const {
        accepting_graduate_student,
    } = generalInfo.recruitment;

    const onValuesChange = (changedValues: any, allValues: any) => {
        const key = Object.keys(changedValues)[0];
        setInputLengths((prevInputLengths: GeneralInfoInputLengths) => ({
            ...prevInputLengths,
            [key]: changedValues[key].length
        }));
    };

    const onFinish = (values: Store) => {
        if (_editorLimitExceeded()) {
            if (researchDescriptionRef?.current) {
                researchDescriptionRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }
        // We need to manually assign editorValue to Form object
        values.description = editorValue;
        dispatch(submitGeneralInfo(
            values, 
            redirectToProfilePage
        ));
    };

    const redirectToProfilePage = () => {
        user !== null && history.push(`/profile/${user.random_id}`);
    };

    const _editorLimitExceeded = () => {
        return editorValue && editorValue.length > TINYMCE_EDITOR_HTML_STRING_LIMIT;
    };

    const Extra = ({ message, inputLength, maxLength }: any) => (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
        }}>
            <span style={{ color: 'initial' }}>
                {inputLengths.interests && message ? <span>{message}</span> : ''}
            </span>
            <span style={{ textAlign: 'right', color: inputLength > maxLength ? 'red' : 'initial' }}>
                {inputLength || 0} / {maxLength} CHARACTERS
            </span>
        </div>
    );

    if (error) {
        return <Error500 extra={<ToHomeButton/>}/>;
    } else {
        return <>
            <Typography.Title 
                level={3} 
                className="sub-heading-font"
                style={{textAlign: 'center'}}
            >
                General Information
            </Typography.Title>

            <Spin spinning={loading}> <Form 
                labelCol={{
                    xs: { span: 24 },
                    sm: { span: 24 },
                    md: { span: 8 },
                    lg: { span: 7 },
                    xl: { span: 6 },
                }}
                wrapperCol={{
                    xs: { span: 24 },
                    sm: { span: 24 },
                    md: { span: 15 },
                    lg: { span: 14 },
                }}

                className="settingpage sub-heading-font"
                onFinish={onFinish}
                onValuesChange={onValuesChange}
                form={form}
                scrollToFirstError={{ behavior: 'smooth' }}
            >
                <Form.Item
                    label="Preferred First Name"
                    name="preferred_first_name"
                    rules={[{ required: true, message: 'Please input your preferred first name.' }, { max: RESEARCHER_PREFERRED_FIRST_NAME_MAX_LENGTH, message: 'Character limit exceeded.' }]}
                    extra={<Extra inputLength={inputLengths.preferred_first_name} maxLength={RESEARCHER_PREFERRED_FIRST_NAME_MAX_LENGTH} />}
                >
                    <Input
                        placeholder="Preferred first name (displayed publicly)" />
                </Form.Item>

                <Form.Item
                    label="Preferred Last Name"
                    name="preferred_last_name"
                    rules={[{ required: true, message: 'Please input your preferred last name.'} , { max: RESEARCHER_PREFERRED_LAST_NAME_MAX_LENGTH, message: 'Character limit exceeded.' }]}
                    extra={<Extra inputLength={inputLengths.preferred_last_name} maxLength={RESEARCHER_PREFERRED_LAST_NAME_MAX_LENGTH} />}
                >
                    <Input
                        placeholder="Preferred last name (displayed publicly)" />
                </Form.Item>

                <Form.Item
                    label="Accepting Graduate Students"
                    name="accepting_graduate_student"
                    valuePropName="checked"
                >
                    <Switch onChange={checked => dispatch(setAcceptingGraduateStudent(checked))}
                            checkedChildren={<CheckOutlined/>}
                            unCheckedChildren={<CloseOutlined/>}/>
                </Form.Item>

                {accepting_graduate_student && <Form.Item
                    label="Years"
                    name="years"
                >
                    <Select
                        mode="multiple"
                        allowClear
                        tagRender={tagRender}
                        options={
                            recruitmentYearsOptions
                        }
                        placeholder='Always'
                    />
                </Form.Item>}

                {accepting_graduate_student && <Form.Item
                    label="Recruitment Notes"
                    name="note"
                    rules={[{ max: RESEARCHER_NOTE_MAX_LENGTH, message: 'Character limit exceeded.' }]}
                    extra={<Extra inputLength={inputLengths.note} maxLength={RESEARCHER_NOTE_MAX_LENGTH} />}
                >
                    <Input.TextArea
                        placeholder='Note for candidates. E.g. accepting only PHD'
                        rows={textareaRows.note}
                    />
                </Form.Item>}

                <Form.Item
                    label="Research Keywords"
                    name="interests"
                    rules={[
                        { max: RESEARCHER_INTERESTS_MAX_LENGTH, message: 'Character limit exceeded.' }
                    ]}
                    extra={ <Extra 
                            inputLength={inputLengths.interests} 
                            maxLength={RESEARCHER_INTERESTS_MAX_LENGTH} 
                            message="Please separate your keywords by a comma." 
                    /> }
                >
                    <Input.TextArea 
                        placeholder='Research interests or keywords, separated by a comma'
                        rows={textareaRows.interests}
                    />
                </Form.Item>

                <Form.Item
                    label="SFU or Personal Webpage"
                    name="sfu_link"
                    rules={[
                        { max: RESEARCHER_SFU_LINK_MAX_LENGTH, message: 'Character limit exceeded.' }
                    ]}
                    extra={<Extra 
                        inputLength={inputLengths.sfu_link} 
                        maxLength={RESEARCHER_SFU_LINK_MAX_LENGTH} 
                    />}
                >
                    <Input 
                        type="url" 
                        placeholder="Please enter full URL starting with http:// or https://" 
                    />
                </Form.Item>

                <Form.Item
                    label="Show Research Description"
                    name="show_description"
                    valuePropName="checked"
                >
                    <Switch 
                        onChange={checked => dispatch(setShowDescription(checked))}
                        checkedChildren={<CheckOutlined/>}
                        unCheckedChildren={<CloseOutlined/>}
                    />
                </Form.Item>

                {generalInfo.show_description && <Form.Item
                    name='description'
                    label="Research Description">
                    <div ref={researchDescriptionRef}>
                        <TinymceEditor
                            editorValue={editorValue}
                            setEditorValue={setEditorValue}
                            placeholder="Introduction or Research Description"
                        />
                    </div>
                </Form.Item>}

                <Form.Item
                    label="Show Preferred Email"
                    name="show_preferred_email"
                    valuePropName="checked"
                >
                    <Switch 
                        onChange={checked => dispatch(setShowPreferredEmail(checked))}
                        checkedChildren={<CheckOutlined/>}
                        unCheckedChildren={<CloseOutlined/>}
                    />
                </Form.Item>

                {generalInfo.show_preferred_email && <Form.Item
                    label="Preferred Email"
                    name="preferred_email"
                    rules={[
                        { max: RESEARCHER_PREFERRED_EMAIL_MAX_LENGTH, message: 'Character limit exceeded.' }, 
                        { type: 'email', message: 'Please input a valid email.' }
                    ]}
                    extra={
                        <Extra 
                            inputLength={inputLengths.preferred_email} 
                            maxLength={RESEARCHER_PREFERRED_EMAIL_MAX_LENGTH } 
                        />
                    }
                >
                    <Input type="email" placeholder='Preferred email'/>
                </Form.Item>}

                <Form.Item
                    label="Show Preferred Pronouns"
                    name="show_preferred_pronouns"
                    valuePropName="checked"
                >
                    <Switch 
                        onChange={checked => dispatch(setShowPreferredPronouns(checked))}
                        checkedChildren={<CheckOutlined/>}
                        unCheckedChildren={<CloseOutlined/>}
                    />
                </Form.Item>

                {generalInfo.show_preferred_pronouns && <Form.Item
                    label="Preferred Pronouns"
                    name="preferred_pronouns"
                    rules={[{ max: RESEARCHER_PREFERRED_PRONOUNS_MAX_LENGTH, message: 'Character limit exceeded.' }]}
                    extra={<Extra inputLength={inputLengths.preferred_pronouns} maxLength={RESEARCHER_PREFERRED_PRONOUNS_MAX_LENGTH} />}
                >
                    <Input type="text" placeholder='e.g. she, her, hers'/>
                </Form.Item>}

                <Form.Item
                    label="Show Primary Campus"
                    name="show_primary_campus"
                    valuePropName="checked"
                >
                    <Switch onChange={checked => dispatch(setShowPrimaryCampus(checked))}
                            checkedChildren={<CheckOutlined/>}
                            unCheckedChildren={<CloseOutlined/>}/>
                </Form.Item>

                {generalInfo.show_primary_campus && <Form.Item
                    name='primary_campus'
                    label="Primary Campus"
                >
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Please select primary campus">
                        {primaryCampusOptions}
                    </Select>
                </Form.Item>}

                <Form.Item
                    label="Show Languages"
                    name="show_languages"
                    valuePropName="checked"
                >
                    <Switch 
                        onChange={checked => dispatch(setShowLanguages(checked))}
                        checkedChildren={<CheckOutlined/>}
                        unCheckedChildren={<CloseOutlined/>}
                    />
                </Form.Item>

                {generalInfo.show_languages && <Form.Item
                    name='languages'
                    label="Languages"
                >
                    <Select
                        mode="multiple"
                        style={{width: '100%'}}
                        placeholder="Please select languages">
                        {languageOptions}
                    </Select>
                </Form.Item>}

                <Form.Item
                    label="Identify as CI researcher"
                    name="C3I"
                    valuePropName="checked"
                >
                    <Switch/>
                </Form.Item>

                <Form.Item
                    wrapperCol={{offset: 8, span: 8}}
                >
                    <Button type="primary" htmlType="submit" style={{marginRight: '15px'}}>
                        Save
                    </Button>
                    <Button htmlType="button" onClick={redirectToProfilePage}>
                        Cancel
                    </Button>
                </Form.Item>

            </Form></Spin>
        </>;
    }

};